import { useCallback } from 'react';
import { MoreHorizontal } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export interface TableAction {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  variant?: 'default' | 'destructive';
}

export interface TableActionsMenuProps {
  actions: TableAction[];
  onTriggerClick?: (e: React.MouseEvent) => void;
}

export function TableActionsMenu({
  actions,
  onTriggerClick,
}: TableActionsMenuProps): JSX.Element {
  const handleTriggerClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onTriggerClick?.(e);
    },
    [onTriggerClick],
  );

  const handleActionClick = useCallback(
    (action: TableAction) => {
      return (e: React.MouseEvent) => {
        e.stopPropagation();
        action.onClick();
      };
    },
    [],
  );

  if (actions.length === 0) {
    return <></>;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" onClick={handleTriggerClick}>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {actions.map((action, index) => {
          const Icon = action.icon;
          return (
            <DropdownMenuItem
              key={index}
              onClick={handleActionClick(action)}
              className={action.variant === 'destructive' ? 'text-destructive' : undefined}
            >
              <Icon className="mr-2 h-4 w-4" />
              {action.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
